import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IAccountGroupResponse } from '../models/account-group.model';
import { AccountGroupApiService } from '../services/account-group-api.service';
import {
  CreateAccountGroup,
  DeleteAccountGroup,
  LoadAccountGroupById,
  LoadAccountGroups,
  LoadAllAccountGroups,
  UpdateAccountGroup,
  ClearCurrentAccountGroup,
} from './account-group.actions';

export interface AccountGroupStateModel {
  accountGroupsLoaded: boolean;
  accountGroups: IAccountGroupResponse[];
  allAccountGroups: IAccountGroupResponse[];
  currentAccountGroup: IAccountGroupResponse | undefined;
  accountGroupsTotalPages: number;
}

const ACCOUNT_GROUP_STATE_TOKEN = new StateToken<AccountGroupStateModel>('accountGroup');

@State<AccountGroupStateModel>({
  name: ACCOUNT_GROUP_STATE_TOKEN,
  defaults: {
    accountGroupsLoaded: false,
    accountGroups: [],
    allAccountGroups: [],
    accountGroupsTotalPages: 0,
    currentAccountGroup: undefined,
  },
})
@Injectable()
export class AccountGroupState {
  constructor(private service: AccountGroupApiService) {}

  @Action(LoadAccountGroups)
  loadAccountGroups(
    { getState, setState }: StateContext<AccountGroupStateModel>,
    { filter }: LoadAccountGroups
  ) {
    return this.service.loadAccountGroups(filter).pipe(
      tap((response: ApiResponse<IAccountGroupResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          accountGroupsLoaded: true,
          accountGroups: response.data,
          accountGroupsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllAccountGroups, { cancelUncompleted: true })
  loadAllAccountGroups(ctx: StateContext<AccountGroupStateModel>) {
    return this.service.loadAllAccountGroups().pipe(
      tap((allAccountGroups: IAccountGroupResponse[]) => {
        ctx.setState(patch({ allAccountGroups }));
      })
    );
  }

  @Action(LoadAccountGroupById, { cancelUncompleted: true })
  loadAccountGroupById(
    { setState }: StateContext<AccountGroupStateModel>,
    { id }: LoadAccountGroupById
  ) {
    return this.service.loadAccountGroupById(id).pipe(
      tap((accountGroup: IAccountGroupResponse) => {
        setState(
          patch({
            currentAccountGroup: accountGroup,
          })
        );
      })
    );
  }

  @Action(CreateAccountGroup)
  @OnSuccess({ url: AdminOrderRoutes.AccountGroups, message: 'accountGroupCreated' })
  createAccountGroup(
    { setState }: StateContext<AccountGroupStateModel>,
    { accountGroup }: CreateAccountGroup
  ) {
    return this.service.createAccountGroup(accountGroup).pipe(
      tap((response: IAccountGroupResponse) => {
        setState(
          patch({
            accountGroups: insertItem<IAccountGroupResponse>(response),
          })
        );
      })
    );
  }

  @Action(UpdateAccountGroup)
  @OnSuccess({ url: AdminOrderRoutes.AccountGroups, message: 'accountGroupUpdated' })
  updateAccountGroup(
    { setState }: StateContext<AccountGroupStateModel>,
    { accountGroup }: UpdateAccountGroup
  ) {
    return this.service.updateAccountGroup(accountGroup);
  }

  @Action(DeleteAccountGroup, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.AccountGroups,
    message: 'accountGroupDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteAccountGroup(ctx: StateContext<AccountGroupStateModel>, { id }: DeleteAccountGroup) {
    return this.service.deleteAccountGroup(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            accountGroups: removeItem<IAccountGroupResponse>(
              (jtToDelete: IAccountGroupResponse | undefined) => jtToDelete?.id === id
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentAccountGroup)
  clearCurrentAccountGroup(ctx: StateContext<AccountGroupStateModel>) {
    ctx.setState(patch({ currentAccountGroup: undefined }));
  }

  @Selector()
  static totalPages(state: AccountGroupStateModel) {
    return state.accountGroupsTotalPages;
  }

  @Selector()
  static accountGroups(state: AccountGroupStateModel) {
    return state.accountGroups;
  }

  @Selector()
  static allAccountGroups(state: AccountGroupStateModel): IAccountGroupResponse[] {
    return state.allAccountGroups;
  }

  @Selector()
  static getCurrentAccountGroup(state: AccountGroupStateModel) {
    return state.currentAccountGroup;
  }
}
